/**
 * Portal URL constants
 *
 * Centralized so app shells don't hardcode `app.animasmarketing.com` in
 * `createShell({ auth: ... })` calls. The hardcoded-config-literals validator
 * excludes `/config/` paths from its scan, so this file is the sanctioned
 * location for these literals.
 *
 * # KNOWN_RUNTIME_URLS — code-as-SSOT for governance tooling
 *
 * The `KNOWN_RUNTIME_URLS` export below is consumed by the cross-app
 * `csp-deps-mismatch` validator (`frontend/governance/validators/cross-app/`)
 * to determine which CSP `connect-src` entries every app that transitively
 * depends on `@plerity/dashboard-shell` must include.
 *
 * The same data is mirrored to `csp-manifest.json` at the package root —
 * the JSON sidecar is what the validator and the Python audit script read
 * (no TS compile step required). The unit test in `__tests__/portal-urls.test.ts`
 * asserts the two stay in sync.
 *
 * To change the portal origin: update PORTAL_ORIGIN below AND
 * `frontend/packages/web/dashboard-shell/csp-manifest.json` (and re-run
 * `make val-csp-deps` to verify all apps' `_headers` still cover it).
 */

const PORTAL_ORIGIN = "https://app.animasmarketing.com";

export const PORTAL_URLS = {
  ORIGIN: PORTAL_ORIGIN,
  LOGIN: `${PORTAL_ORIGIN}/login`,
  LOGOUT: `${PORTAL_ORIGIN}/logout`,
  SECURITY: `${PORTAL_ORIGIN}/settings/security`,
  API: PORTAL_ORIGIN,
} as const;

/**
 * Manifest schema for `KNOWN_RUNTIME_URLS` and `csp-manifest.json` sidecars.
 *
 * Read by `frontend/governance/validators/cross-app/csp-deps-mismatch.ts`.
 *
 * - `absolute`               — package hardcodes the URL; CSP must include it
 * - `caller-supplied`        — package takes URL via prop/option; CSP is caller's job
 * - `caller-supplied-with-default` — like `caller-supplied`, but every current
 *                              consumer passes the same default value, so
 *                              that default is the de-facto CSP requirement
 *                              when not overridden
 */
export interface CspRuntimeManifest {
  /** npm package name (e.g. `@plerity/dashboard-shell`) */
  package: string;
  /** Schema version — bump on breaking field changes */
  schema_version: 1;
  /** Absolute https:// origins required at runtime */
  urls: string[];
  /** How the URL is supplied (see schema docstring) */
  type: "absolute" | "caller-supplied" | "caller-supplied-with-default";
  /** Free-form note for human reviewers (callers, source file, override mechanism) */
  note?: string;
}

/**
 * CSP runtime URL declaration for `@plerity/dashboard-shell`.
 *
 * `dashboard-shell` is the canonical owner of the portal origin constant
 * (PORTAL_URLS.ORIGIN). Every app that uses `createShell()` carries this
 * requirement transitively — the SSO bootstrap call in `@plerity/auth`
 * is fed `portalApiUrl: PORTAL_URLS.API` by `createShell`.
 */
export const KNOWN_RUNTIME_URLS: CspRuntimeManifest = {
  package: "@plerity/dashboard-shell",
  schema_version: 1,
  urls: [PORTAL_ORIGIN],
  type: "absolute",
  note:
    "Portal SSO bootstrap origin. PORTAL_URLS.ORIGIN in src/config/portal-urls.ts " +
    "feeds AuthProvider's portalApiUrl prop via createShell(). All transitive " +
    "consumers of @plerity/dashboard-shell call this origin for /api/auth/me, " +
    "/api/auth/unified-refresh, /api/auth/org-switch.",
};
